import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, collection, query, where, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Room, V3Event, V3User, V3Submission, V3ProblemStatus } from "../types/v3types";
import styled from "styled-components";

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ControlPanel = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SidePanel = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin: 0 0 16px 0;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" | "success" | "danger" }>`
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  
  ${props => {
    switch (props.variant) {
      case "primary":
        return `
          background-color: #1976d2;
          color: white;
          &:hover { background-color: #1565c0; }
        `;
      case "success":
        return `
          background-color: #388e3c;
          color: white;
          &:hover { background-color: #2e7d32; }
        `;
      case "danger":
        return `
          background-color: #d32f2f;
          color: white;
          &:hover { background-color: #c62828; }
        `;
      default:
        return `
          background-color: #e0e0e0;
          color: #333;
          &:hover { background-color: #d5d5d5; }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin: 16px 0;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case "waiting":
        return `
          background-color: #fff3e0;
          color: #e65100;
        `;
      case "running":
        return `
          background-color: #e8f5e9;
          color: #2e7d32;
        `;
      case "finished":
        return `
          background-color: #f3e5f5;
          color: #6a1b9a;
        `;
      default:
        return `
          background-color: #f5f5f5;
          color: #666;
        `;
    }
  }}
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 12px;
  margin: 16px 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const CurrentProblemInfo = styled.div`
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
`;

const ProblemTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px 0;
`;

const ImageControls = styled.div`
  margin-top: 12px;
`;

const ImageInfo = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`;

const ParticipantList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const ParticipantItem = styled.div`
  padding: 12px;
  margin: 8px 0;
  background: #f5f5f5;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ParticipantName = styled.div`
  font-weight: 500;
`;

const ParticipantScore = styled.div`
  font-size: 14px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
`;

const CorrectAnswersList = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #f0f8ff;
  border-radius: 8px;
  border: 1px solid #1976d2;
`;

const CorrectAnswersTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #1976d2;
  font-size: 16px;
`;

const CorrectAnswerItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const AnswerTime = styled.span`
  font-size: 14px;
  color: #666;
`;

const AnswerScore = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #28a745;
`;

const AccessCodeDisplay = styled.div`
  text-align: center;
  padding: 20px;
  background: #e3f2fd;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const AccessCode = styled.div`
  font-size: 32px;
  font-weight: bold;
  letter-spacing: 4px;
  color: #1976d2;
  margin-top: 10px;
`;

const AdminRoomManager: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId?: string; roomId: string }>();
  const navigate = useNavigate();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [participants, setParticipants] = useState<V3User[]>([]);
  const [submissions, setSubmissions] = useState<V3Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [problemTimers, setProblemTimers] = useState<{ [problemId: string]: number }>({});

  // Initialize timers on room/event load
  useEffect(() => {
    if (room && event) {
      const timers: { [problemId: string]: number } = {};
      event.problems.forEach(problem => {
        const problemId = problem.problemId;
        const status = room.problemStatuses?.[problemId];
        
        if (status === "timing" && room.problemStartTimes?.[problemId]) {
          // Calculate remaining time based on server start time
          const startTime = room.problemStartTimes[problemId].toMillis();
          const currentTime = Date.now();
          const elapsed = Math.round((currentTime - startTime) / 1000);
          const remaining = Math.max(0, problem.timeLimit - elapsed);
          timers[problemId] = remaining;
        } else if (room.problemTimers?.[problemId]) {
          timers[problemId] = room.problemTimers[problemId];
        } else {
          timers[problemId] = problem.timeLimit;
        }
      });
      setProblemTimers(timers);
    }
  }, [room, event]);

  useEffect(() => {
    if (!roomId) return;

    let unsubscribeRoom: (() => void) | undefined;
    let unsubscribeParticipants: (() => void) | undefined;
    let unsubscribeSubmissions: (() => void) | undefined;

    // Load room data
    const loadRoomData = async () => {
      try {
        const roomDoc = await getDoc(doc(db, "v3rooms", roomId));
        if (roomDoc.exists()) {
          const roomData = roomDoc.data() as V3Room;
          setRoom(roomData);
          
          // Use project ID from room data if not provided in URL
          const currentProjectId = projectId || roomData.projectId;

          // Load event data
          const eventDoc = await getDoc(doc(db, "v3events", roomData.eventId));
          if (eventDoc.exists()) {
            setEvent(eventDoc.data() as V3Event);
          }

          // Subscribe to room updates
          unsubscribeRoom = onSnapshot(doc(db, "v3rooms", roomId), (docSnapshot) => {
            if (docSnapshot.exists()) {
              const updatedRoom = docSnapshot.data() as V3Room;
              setRoom(updatedRoom);
              
              // Update participants when room participants change
              if (updatedRoom.participants) {
                const participantsQuery = query(
                  collection(db, "v3users"),
                  where("projectId", "==", currentProjectId)
                );
                
                onSnapshot(participantsQuery, (snapshot) => {
                  const users = snapshot.docs
                    .map(doc => doc.data() as V3User)
                    .filter(user => updatedRoom.participants?.includes(user.userId));
                  setParticipants(users);
                });
              }
            }
          });

          // Subscribe to participants
          const participantsQuery = query(
            collection(db, "v3users"),
            where("projectId", "==", currentProjectId)
          );
          
          unsubscribeParticipants = onSnapshot(participantsQuery, (snapshot) => {
            const users = snapshot.docs
              .map(doc => doc.data() as V3User)
              .filter(user => roomData.participants?.includes(user.userId));
            setParticipants(users);
          });

          // Subscribe to submissions
          const submissionsQuery = query(
            collection(db, "v3submissions"),
            where("roomId", "==", roomId)
          );
          
          unsubscribeSubmissions = onSnapshot(submissionsQuery, (snapshot) => {
            const subs = snapshot.docs.map(doc => doc.data() as V3Submission);
            setSubmissions(subs);
          });
        } else {
          if (projectId) {
            navigate(`/admin/v3/projects/${projectId}`);
          } else {
            navigate('/admin/v3/projects');
          }
        }
      } catch (error) {
        console.error("Error loading room data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRoomData();

    return () => {
      if (unsubscribeRoom) unsubscribeRoom();
      if (unsubscribeParticipants) unsubscribeParticipants();
      if (unsubscribeSubmissions) unsubscribeSubmissions();
    };
  }, [projectId, roomId, navigate]);

  const updateRoom = async (updates: Partial<V3Room>) => {
    if (!roomId) return;
    
    try {
      await updateDoc(doc(db, "v3rooms", roomId), {
        ...updates,
        lastUpdate: serverTimestamp()
      });
    } catch (error) {
      console.error("Error updating room:", error);
    }
  };

  const startRoom = () => {
    if (!event) return;
    
    const firstProblemId = event.problems[0]?.problemId;
    const updates: Partial<V3Room> = {
      status: "running",
      startTime: serverTimestamp() as any,
      displayMode: "countdown"
    };
    
    // Set first problem to waiting state
    if (firstProblemId) {
      updates.problemStatuses = {
        ...(room?.problemStatuses || {}),
        [firstProblemId]: "waiting"
      };
    }
    
    updateRoom(updates);
  };

  const setProblemStatus = (problemId: string, status: V3ProblemStatus) => {
    if (!room || !event) return;
    
    const problem = event.problems.find(p => p.problemId === problemId);
    if (!problem) return;
    
    const newStatuses = {
      ...(room.problemStatuses || {}),
      [problemId]: status
    };
    
    const updates: Partial<V3Room> = {
      problemStatuses: newStatuses
    };
    
    // If starting timer, save current time
    if (status === "timing") {
      updates.problemTimers = {
        ...(room.problemTimers || {}),
        [problemId]: problem.timeLimit
      };
      updates.problemStartTimes = {
        ...(room.problemStartTimes || {}),
        [problemId]: serverTimestamp() as any
      };
      updates.displayMode = "problemWithTimer";
      setProblemTimers(prev => ({ ...prev, [problemId]: problem.timeLimit }));
    } else if (status === "waiting") {
      updates.displayMode = "problem";
    } else if (status === "finished") {
      updates.displayMode = "problem";
      // Save the final timer value
      const currentTimer = problemTimers[problemId] || 0;
      updates.problemTimers = {
        ...(room.problemTimers || {}),
        [problemId]: currentTimer
      };
    }
    
    updateRoom(updates);
  };

  // Timer countdown for admin display
  useEffect(() => {
    if (!room || !event) return;
    
    const currentProblem = event.problems[room.currentProblemIndex];
    if (!currentProblem) return;
    
    const problemId = currentProblem.problemId;
    const status = room.problemStatuses?.[problemId];
    
    if (status === "timing" && problemTimers[problemId] > 0) {
      const interval = setInterval(() => {
        setProblemTimers(prev => {
          // Check if status is still "timing" before decrementing
          if (room.problemStatuses?.[problemId] !== "timing") {
            clearInterval(interval);
            return prev;
          }
          
          const newTimer = (prev[problemId] || 0) - 1;
          if (newTimer <= 0) {
            clearInterval(interval);
            return { ...prev, [problemId]: 0 };
          }
          
          // Update Firestore every 10 seconds to sync timer (reduce writes)
          if (newTimer % 10 === 0) {
            updateRoom({
              problemTimers: {
                ...(room.problemTimers || {}),
                [problemId]: newTimer
              }
            });
          }
          
          return { ...prev, [problemId]: newTimer };
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [room, event, problemTimers, updateRoom]);

  const finishRoom = () => {
    updateRoom({
      status: "finished",
      endTime: serverTimestamp() as any,
      displayMode: "finalRanking"
    });
  };

  const nextProblem = () => {
    if (!event || !room) return;
    
    const nextIndex = Math.min(room.currentProblemIndex + 1, event.problems.length - 1);
    const nextProblemId = event.problems[nextIndex].problemId;
    
    updateRoom({
      currentProblemIndex: nextIndex,
      currentImageIndex: 0,
      displayMode: "problem",
      problemStatuses: {
        ...(room.problemStatuses || {}),
        [nextProblemId]: "waiting"
      }
    });
    
    // Reset local timer for the new problem
    setProblemTimers(prev => ({
      ...prev,
      [nextProblemId]: event.problems[nextIndex].timeLimit
    }));
  };

  const previousProblem = () => {
    if (!event || !room) return;
    
    const prevIndex = Math.max(room.currentProblemIndex - 1, 0);
    const prevProblemId = event.problems[prevIndex].problemId;
    
    updateRoom({
      currentProblemIndex: prevIndex,
      currentImageIndex: 0,
      displayMode: "problem",
      problemStatuses: {
        ...(room.problemStatuses || {}),
        [prevProblemId]: "waiting"
      }
    });
    
    // Reset local timer for the previous problem
    setProblemTimers(prev => ({
      ...prev,
      [prevProblemId]: event.problems[prevIndex].timeLimit
    }));
  };

  const nextImage = () => {
    if (!event || !room) return;
    
    const currentProblem = event.problems[room.currentProblemIndex];
    const allImages = [...(currentProblem.beforeImageUrls || []), ...currentProblem.imageUrls];
    const currentIndex = room.currentImageIndex || 0;
    const nextIndex = (currentIndex + 1) % allImages.length;
    
    updateRoom({
      currentImageIndex: nextIndex
    });
  };

  const previousImage = () => {
    if (!event || !room) return;
    
    const currentProblem = event.problems[room.currentProblemIndex];
    const allImages = [...(currentProblem.beforeImageUrls || []), ...currentProblem.imageUrls];
    const currentIndex = room.currentImageIndex || 0;
    const nextIndex = currentIndex === 0 ? allImages.length - 1 : currentIndex - 1;
    
    updateRoom({
      currentImageIndex: nextIndex
    });
  };

  const showAnswer = () => {
    updateRoom({
      displayMode: "answer"
    });
  };

  const showRanking = () => {
    updateRoom({
      displayMode: "ranking"
    });
  };

  if (loading) {
    return (
      <Container>
        <div>読み込み中...</div>
      </Container>
    );
  }

  if (!room || !event) {
    return (
      <Container>
        <EmptyState>ルームが見つかりません</EmptyState>
      </Container>
    );
  }

  const currentProblem = event.problems[room.currentProblemIndex];
  const allImages = [...(currentProblem?.beforeImageUrls || []), ...(currentProblem?.imageUrls || [])];
  const currentImageIndex = room.currentImageIndex || 0;

  // Get correct submissions for current problem
  const correctSubmissions = currentProblem ? 
    submissions
      .filter(sub => sub.problemId === currentProblem.problemId && sub.isCorrect)
      .sort((a, b) => a.timeTaken - b.timeTaken) // Sort by time taken (faster first)
    : [];
  
  const getUsername = (userId: string) => {
    const user = participants.find(p => p.userId === userId);
    return user?.username || 'Unknown';
  };

  // Calculate scores
  const participantScores = participants.map(participant => {
    const userSubmissions = submissions.filter(sub => sub.userId === participant.userId);
    const totalScore = userSubmissions.reduce((sum, sub) => sum + (sub.score || 0), 0);
    return { ...participant, totalScore };
  }).sort((a, b) => b.totalScore - a.totalScore);

  const currentProjectId = projectId || room.projectId;

  return (
    <Container>
      <Header>
        <Title>{room.roomName}</Title>
        <HeaderButtons>
          <Button onClick={() => navigate(`/admin/v3/projects/${currentProjectId}`)}>
            プロジェクトに戻る
          </Button>
          <Button 
            variant="primary"
            onClick={() => window.open(`/v3/${currentProjectId}/room/${roomId}/screen`, '_blank')}
          >
            スクリーンを開く
          </Button>
        </HeaderButtons>
      </Header>

      <MainGrid>
        <div>
          <ControlPanel>
            <SectionTitle>ルーム管理</SectionTitle>
            
            <InfoGrid>
              <InfoRow>
                <InfoLabel>ステータス</InfoLabel>
                <StatusBadge status={room.status}>{room.status}</StatusBadge>
              </InfoRow>
              <InfoRow>
                <InfoLabel>イベント</InfoLabel>
                <InfoValue>{event.eventName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>参加者数</InfoLabel>
                <InfoValue>{participantScores.length}人</InfoValue>
              </InfoRow>
            </InfoGrid>

            {!room.isPublic && room.accessCode && (
              <AccessCodeDisplay>
                <InfoLabel>アクセスコード</InfoLabel>
                <AccessCode>{room.accessCode}</AccessCode>
              </AccessCodeDisplay>
            )}

            <ButtonGroup>
              {room.status === "waiting" && (
                <Button variant="success" onClick={startRoom}>
                  ゲーム開始
                </Button>
              )}
              {room.status === "running" && (
                <>
                  <Button variant="danger" onClick={finishRoom}>
                    ゲーム終了
                  </Button>
                  <Button onClick={() => updateRoom({ status: "paused" })}>
                    一時停止
                  </Button>
                </>
              )}
              {room.status === "paused" && (
                <Button variant="success" onClick={() => updateRoom({ status: "running" })}>
                  再開
                </Button>
              )}
            </ButtonGroup>
          </ControlPanel>

          {room.status !== "waiting" && currentProblem && (
            <ControlPanel style={{ marginTop: "20px" }}>
              <SectionTitle>問題管理</SectionTitle>
              
              <CurrentProblemInfo>
                <ProblemTitle>
                  問題 {room.currentProblemIndex + 1} / {event.problems.length}
                </ProblemTitle>
                <InfoLabel>{currentProblem.title}</InfoLabel>
                
                <div style={{ margin: "15px 0" }}>
                  <InfoLabel>問題ステータス: </InfoLabel>
                  <strong>
                    {room.problemStatuses?.[currentProblem.problemId] === "timing" ? "タイマー進行中" :
                     room.problemStatuses?.[currentProblem.problemId] === "finished" ? "終了" : "待機中"}
                  </strong>
                  {room.problemStatuses?.[currentProblem.problemId] === "timing" && (
                    <span style={{ marginLeft: "15px", fontSize: "18px", fontWeight: "bold", color: "#dc143c" }}>
                      残り時間: {Math.floor((problemTimers[currentProblem.problemId] || 0) / 60)}:
                      {String((problemTimers[currentProblem.problemId] || 0) % 60).padStart(2, '0')}
                    </span>
                  )}
                </div>
                
                {allImages.length > 0 && (
                  <ImageControls>
                    <ImageInfo>
                      画像 {currentImageIndex + 1} / {allImages.length}
                    </ImageInfo>
                    <ButtonGroup>
                      <Button onClick={previousImage} disabled={allImages.length <= 1}>
                        前の画像
                      </Button>
                      <Button onClick={nextImage} disabled={allImages.length <= 1}>
                        次の画像
                      </Button>
                    </ButtonGroup>
                  </ImageControls>
                )}
              </CurrentProblemInfo>

              <ButtonGroup>
                <Button 
                  variant={room.problemStatuses?.[currentProblem.problemId] === "waiting" || !room.problemStatuses?.[currentProblem.problemId] ? "primary" : "secondary"}
                  onClick={() => setProblemStatus(currentProblem.problemId, "waiting")}
                  disabled={room.problemStatuses?.[currentProblem.problemId] === "waiting"}
                >
                  待機
                </Button>
                <Button 
                  variant={room.problemStatuses?.[currentProblem.problemId] === "timing" ? "success" : "secondary"}
                  onClick={() => setProblemStatus(currentProblem.problemId, "timing")}
                  disabled={room.problemStatuses?.[currentProblem.problemId] === "timing"}
                >
                  タイマースタート
                </Button>
                <Button 
                  variant={room.problemStatuses?.[currentProblem.problemId] === "finished" ? "danger" : "secondary"}
                  onClick={() => setProblemStatus(currentProblem.problemId, "finished")}
                  disabled={room.problemStatuses?.[currentProblem.problemId] === "finished"}
                >
                  終了
                </Button>
              </ButtonGroup>

              <ButtonGroup>
                <Button 
                  onClick={previousProblem} 
                  disabled={room.currentProblemIndex === 0}
                >
                  前の問題
                </Button>
                <Button 
                  variant="primary"
                  onClick={nextProblem} 
                  disabled={room.currentProblemIndex >= event.problems.length - 1}
                >
                  次の問題
                </Button>
              </ButtonGroup>

              <ButtonGroup>
                <Button onClick={showAnswer}>
                  解答を表示
                </Button>
                <Button onClick={showRanking}>
                  ランキングを表示
                </Button>
              </ButtonGroup>
              
              {correctSubmissions.length > 0 && (
                <CorrectAnswersList>
                  <CorrectAnswersTitle>正解者 ({correctSubmissions.length}名)</CorrectAnswersTitle>
                  {correctSubmissions.map((sub, index) => (
                    <CorrectAnswerItem key={sub.submissionId}>
                      <div>
                        <strong>{index + 1}.</strong> {getUsername(sub.userId)}
                      </div>
                      <div>
                        <AnswerTime>{sub.timeTaken.toFixed(3)}秒</AnswerTime>
                        {' '}
                        <AnswerScore>+{sub.score}点</AnswerScore>
                      </div>
                    </CorrectAnswerItem>
                  ))}
                </CorrectAnswersList>
              )}
            </ControlPanel>
          )}
        </div>

        <SidePanel>
          <SectionTitle>参加者 ({participantScores.length})</SectionTitle>
          
          {participantScores.length > 0 ? (
            <ParticipantList>
              {participantScores.map((participant, index) => (
                <ParticipantItem key={participant.userId}>
                  <ParticipantName>
                    {index + 1}. {participant.username}
                  </ParticipantName>
                  <ParticipantScore>
                    {participant.totalScore}点
                  </ParticipantScore>
                </ParticipantItem>
              ))}
            </ParticipantList>
          ) : (
            <EmptyState>
              まだ参加者がいません
            </EmptyState>
          )}
        </SidePanel>
      </MainGrid>
    </Container>
  );
};

export default AdminRoomManager;